import * as z from 'zod';

import { validationMessagesMap } from 'src/utils/messages';

export enum PAYMENT_PROVIDERS {
    DSK = 'DSK',
    MyPOS = 'MyPOS',
}

export const formSchema = z
    .object({
        are_borica_payments_enabled: z.boolean().optional(),
        serial_id: z.string().optional(),
        has_own_payment_provider: z.boolean().optional(),
        own_payment_provider: z.nativeEnum(PAYMENT_PROVIDERS).optional(),
        username: z.string().optional(),
        password: z.string().optional(),
        store_id: z.string().optional(),
        client_number: z.string().optional(),
        key_index: z.coerce.number().int().min(0).optional(),
        public_certificate: z.instanceof(File).optional(),
        private_key: z.instanceof(File).optional(),
        subdomain: z.string().optional(),
        name: z.string().optional(),
        address: z.string().optional(),
        uic: z.string().optional(),
        tax_number: z.string().optional(),
        email: z.string().email().nullable().optional(),
        phone: z.string().nullable().optional(),
        _isEditMode: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
        const hasEmail = !!data.email;
        const hasPhone = !!data.phone;

        if (!data.has_own_payment_provider) {
            return;
        }
        if (data.has_own_payment_provider) {
            if (!data.own_payment_provider) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['own_payment_provider'],
                });
            }
            if (!data.name) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['name'],
                });
            }
            if (!data.address) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['address'],
                });
            }
            if (!data.uic) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['uic'],
                });
            }
            if (!data.tax_number) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['tax_number'],
                });
            }
            if (!hasEmail && !hasPhone) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['email'],
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['phone'],
                });
            }
        }
        if (data.own_payment_provider === PAYMENT_PROVIDERS.DSK) {
            if (!data.username?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['username'],
                });
            }
            if (!data.password?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['password'],
                });
            }
            return;
        }
        if (data.own_payment_provider === PAYMENT_PROVIDERS.MyPOS) {
            const isCreateMode = !data._isEditMode;

            if (isCreateMode && !data.public_certificate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['public_certificate'],
                });
            }
            if (isCreateMode && !data.private_key) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['private_key'],
                });
            }
            if (!data.store_id) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['store_id'],
                });
            }
            if (!data.client_number) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['client_number'],
                });
            }
            if (!data.key_index) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['key_index'],
                });
            }
            if (!data.subdomain) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorRequired,
                    path: ['subdomain'],
                });
            }
            return;
        }
    });

export type PaymentProviderFormSchemaType = z.infer<typeof formSchema>;
